import type { CharacteristicSelection, SelectionsMap } from './selections';

/**
 * The slice of a rule-evaluation result that decides what the customer can
 * still see. Declared structurally (rather than importing `RuleResult`) so
 * `state/` doesn't have to depend on `rules/` — callers just hand over the
 * whole rule result.
 */
export interface VisibilityRules {
    hiddenCharacteristics: ReadonlySet<number>;
    blacklistedValues: ReadonlySet<number>;
}

/**
 * Project the raw customer selections onto what the rules currently allow.
 *
 * A rule that removes a Characteristic has to take its value out of *every*
 * derived path — total, formulas, asset payload, combined image — not just
 * the option list the UI renders. Legacy did this by destroying the pick:
 * `setIsBlacklisted(true)` calls `setActiveChoice(null)` + `setValue(null)`
 * (option/abstract.js:1158-1160) and `calculatePrice()` returns 0 while
 * `getIsBlacklisted()` (:1651-1654). V3 computes the same "no value, no
 * price" state as a projection instead, so every consumer can read one
 * effective map rather than re-deriving the exclusion locally.
 *
 *   - Characteristic hidden by a rule → entry drops out entirely
 *   - blacklisted Value ids → removed from `single` / `multi` / `quantities`
 *   - a selection emptied that way → entry drops out too, NOT `valueId: null`:
 *     `resolveSuffix` in eval/evaluator.ts falls through to the
 *     Characteristic-level `price` for a null pick, but correctly yields 0
 *     for a missing entry.
 *
 * Reads *only* the rule results — never the admin's `visibility` field.
 * `hidden` / `backend` mean "don't render", not "don't charge" (a pinned
 * base image with a fixed fee is exactly that use case), and pricing those
 * out was already tried and reverted once.
 *
 * Pure — the store keeps the customer's original pick, so it comes back
 * unchanged the moment the rule stops matching. Returns the input map by
 * identity when there is nothing to strip, so downstream React memos don't
 * churn.
 */
export function effectiveSelections(
    selections: SelectionsMap,
    rules: VisibilityRules,
): SelectionsMap {
    const hidden = rules.hiddenCharacteristics;
    const blacklisted = rules.blacklistedValues;
    if (hidden.size === 0 && blacklisted.size === 0) return selections;

    const out: Record<number, CharacteristicSelection> = {};
    let changed = false;
    for (const [charIdStr, sel] of Object.entries(selections)) {
        const charId = Number(charIdStr);
        if (hidden.has(charId)) {
            changed = true;
            continue;
        }
        const scrubbed = scrubValues(sel, blacklisted);
        if (scrubbed === null) {
            changed = true;
            continue;
        }
        if (scrubbed !== sel) changed = true;
        out[charId] = scrubbed;
    }
    return changed ? out : selections;
}

/**
 * Strip blacklisted Value ids out of one selection. Returns the input by
 * identity when nothing was blacklisted, `null` when nothing survives.
 */
function scrubValues(
    sel: CharacteristicSelection,
    blacklisted: ReadonlySet<number>,
): CharacteristicSelection | null {
    if (blacklisted.size === 0) return sel;

    switch (sel.kind) {
        case 'single':
            // A pick that was never made stays as-is: it's "not chosen yet",
            // which every consumer already handles, and rewriting it would
            // only churn object identity.
            if (sel.valueId === null || !blacklisted.has(sel.valueId)) return sel;
            return null;
        case 'multi': {
            const kept = sel.valueIds.filter((id) => !blacklisted.has(id));
            if (kept.length === sel.valueIds.length) return sel;
            return kept.length === 0 ? null : { kind: 'multi', valueIds: kept };
        }
        case 'quantities': {
            const kept: Record<number, number> = {};
            let dropped = false;
            for (const [idStr, qty] of Object.entries(sel.quantities)) {
                const id = Number(idStr);
                if (blacklisted.has(id)) {
                    dropped = true;
                    continue;
                }
                kept[id] = qty;
            }
            if (!dropped) return sel;
            return Object.keys(kept).length === 0
                ? null
                : { kind: 'quantities', quantities: kept };
        }
        // Free entry / uploads / captcha carry no Value ids to blacklist.
        case 'text':
        case 'files':
        case 'captcha':
            return sel;
    }
}
